import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';

import { getConfigs, updateBaseFieldsConfig, resetBaseFieldsConfig } from '../api';
import { buildRulesForApi } from '../../profiles/hooks/useProfileForm';

const CONFIG_CODE = 'by_client_property_decision_maker';

/**
 * Estrae ruleStrategy e selectedProperties dalla config API.
 */
function parseConfigRules(configData) {
    if (!configData || !configData.rules || configData.rules.length === 0) {
        return {
            ruleStrategy: 'include_all',
            selectedProperties: [],
        };
    }

    const rule = configData.rules[0];

    if (rule.type === 'include' && rule.target === 'properties') {
        return {
            ruleStrategy: 'include_specific',
            selectedProperties: rule.properties || [],
        };
    }

    // Qualsiasi altra combinazione la trattiamo come include_all
    return {
        ruleStrategy: 'include_all',
        selectedProperties: [],
    };
}

/**
 * Hook per la gestione della configurazione campi base.
 */
export function useBaseFieldsConfig(i18n) {
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isResetting, setIsResetting] = useState(false);
    const [initialValues, setInitialValues] = useState({
        ruleStrategy: 'include_all',
        selectedProperties: [],
    });

    const loadConfig = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await getConfigs();
            const configs = response.data?.data || [];
            const config = configs.find(c => c.code === CONFIG_CODE);

            setInitialValues(parseConfigRules(config?.data));
        } catch (err) {
            console.error('Error loading configs:', err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const saveConfig = useCallback(async (formData) => {
        setIsSaving(true);
        try {
            const rules = buildRulesForApi(formData);
            await updateBaseFieldsConfig({ data: { rules } });

            toast(i18n._('basefields:saved'), {
                position: 'bottom-right',
                type: toast.TYPE.SUCCESS,
            });

            // Aggiorna initialValues con i dati appena salvati
            setInitialValues({
                ruleStrategy: formData.ruleStrategy,
                selectedProperties: formData.selectedProperties || [],
            });
        } catch (err) {
            toast(i18n._('error:unexpected'), {
                position: 'bottom-right',
                type: toast.TYPE.ERROR,
            });
        } finally {
            setIsSaving(false);
        }
    }, [i18n]);

    const resetToDefault = useCallback(async () => {
        setIsResetting(true);
        try {
            await resetBaseFieldsConfig();

            toast(i18n._('basefields:resetted'), {
                position: 'bottom-right',
                type: toast.TYPE.SUCCESS,
            });

            // Ricarica la config dopo il reset
            await loadConfig();
        } catch (err) {
            toast(i18n._('error:unexpected'), {
                position: 'bottom-right',
                type: toast.TYPE.ERROR,
            });
        } finally {
            setIsResetting(false);
        }
    }, [i18n, loadConfig]);

    return {
        isLoading,
        isSaving,
        isResetting,
        initialValues,
        loadConfig,
        saveConfig,
        resetToDefault,
    };
}
